import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Home, 
  Clock, 
  Star, 
  ScanLine, 
  MoreHorizontal,
  Trash2,
  FolderOpen,
  Settings,
  Heart,
  Building2,
  Users,
  Link2,
  Bell,
  Wrench,
  Plus,
  Upload,
  X,
  ChevronRight,
  Car
} from "lucide-react";
import { cn } from "@/lib/utils";
import { hapticSelection } from "@/lib/despiaHaptics";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useFolders } from "@/hooks/useFolders";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Main navigation items (Drive-like structure) - Unified Home
const navItems = [
  { path: "/documents", icon: Home, label: "Documents" },
  { path: "/documents?view=recent", icon: Clock, label: "Récents" },
  { path: "/documents?view=starred", icon: Star, label: "Favoris" },
  { path: "/pdf-tools", icon: ScanLine, label: "Scanner" },
];

// Items in the "More" slide panel - removed Tableau de bord (merged into /documents)
const moreItems = [
  { path: "/documents?view=trash", icon: Trash2, label: "Corbeille" },
  { path: "/actions", icon: Bell, label: "Actions" },
  { path: "/demarches", icon: FolderOpen, label: "Démarches" },
  { path: "/fleet", icon: Car, label: "Ma Flotte" },
  { path: "/administrative-health", icon: Heart, label: "Santé administrative" },
  { path: "/legal-documents", icon: Building2, label: "Documents légaux" },
  { path: "/subcontractors", icon: Users, label: "Prestataires" },
  { path: "/share-links", icon: Link2, label: "Partages" },
  { path: "/notifications", icon: Bell, label: "Notifications" },
  { path: "/settings", icon: Settings, label: "Réglages" },
];

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const { folders } = useFolders();

  // Don't show on auth, landing, onboarding, pricing/legal, shared pages, or full-screen detail pages
  const hiddenPaths = ["/", "/auth", "/onboarding", "/pricing", "/privacy", "/terms", "/legal"];
  const isSharedPage = location.pathname.startsWith("/share/");
  const isFullScreenDetail = location.pathname.startsWith("/documents/");
  
  if (hiddenPaths.includes(location.pathname) || isSharedPage || isFullScreenDetail) {
    return null;
  }

  const currentFullPath = `${location.pathname}${location.search}`;
  
  const isNavItemActive = (itemPath: string) => {
    // For items with query params, compare the full path
    if (itemPath.includes("?")) {
      return currentFullPath === itemPath;
    }
    // For /documents without query, it's active only when no view param
    if (itemPath === "/documents") {
      return location.pathname === "/documents" && !location.search;
    }
    return location.pathname === itemPath;
  };

  const isMoreActive = moreItems.some(item => {
    if (item.path.includes("?")) {
      return currentFullPath === item.path;
    }
    return location.pathname === item.path;
  });

  const handleNavigation = (path: string) => {
    hapticSelection();
    navigate(path);
    setSheetOpen(false);
  };

  // Get root folders (no parent)
  const rootFolders = folders?.filter(f => !f.parent_id) || [];

  // Show FAB on documents page (not in trash)
  const showFAB = location.pathname === "/documents" && !location.search.includes("view=trash");

  return (
    <>
      {/* Floating Action Button for upload */}
      {showFAB && (
        <button
          onClick={() => {
            hapticSelection();
            // Trigger file upload - we'll dispatch a custom event
            window.dispatchEvent(new CustomEvent('mobile-upload-trigger'));
          }}
          className="md:hidden fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Ajouter un document"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            const isActive = isNavItemActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-1 flex-1 py-2 px-1 rounded-lg transition-colors touch-manipulation",
                  isActive 
                    ? "text-primary" 
                    : "text-muted-foreground active:text-foreground"
                )}
              >
                <div className="relative">
                  <item.icon className={cn("w-5 h-5", isActive && "scale-110")} />
                </div>
                <span className={cn(
                  "text-[10px] font-medium truncate",
                  isActive && "font-semibold"
                )}>
                  {item.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />
                )}
              </button>
            );
          })}

          {/* More button with Sheet */}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button
                onClick={() => hapticSelection()}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-1 flex-1 py-2 px-1 rounded-lg transition-colors touch-manipulation",
                  isMoreActive 
                    ? "text-primary" 
                    : "text-muted-foreground active:text-foreground"
                )}
              >
                <div className="relative">
                  <MoreHorizontal className={cn("w-5 h-5", isMoreActive && "scale-110")} />
                </div>
                <span className={cn(
                  "text-[10px] font-medium truncate",
                  isMoreActive && "font-semibold"
                )}>
                  Plus
                </span>
                {isMoreActive && (
                  <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
              <SheetHeader className="pb-4">
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              
              <div className="space-y-1 overflow-y-auto max-h-[calc(70vh-80px)]">
                {/* Folders section */}
                {rootFolders.length > 0 && (
                  <Collapsible open={foldersOpen} onOpenChange={setFoldersOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-3 rounded-lg hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <FolderOpen className="w-5 h-5 text-muted-foreground" />
                        <span className="font-medium">Dossiers</span>
                      </div>
                      <ChevronRight className={cn(
                        "w-4 h-4 text-muted-foreground transition-transform",
                        foldersOpen && "rotate-90"
                      )} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-6 space-y-1">
                      {rootFolders.map(folder => (
                        <button
                          key={folder.id}
                          onClick={() => handleNavigation(`/documents?folder=${folder.id}`)}
                          className={cn(
                            "flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors",
                            currentFullPath === `/documents?folder=${folder.id}`
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-muted text-muted-foreground"
                          )}
                        >
                          <FolderOpen className="w-4 h-4" />
                          <span className="truncate">{folder.name}</span>
                        </button>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Divider */}
                {rootFolders.length > 0 && (
                  <div className="my-2 border-t border-border" />
                )}

                {/* More items */}
                {moreItems.map((item) => {
                  const isActive = item.path.includes("?") 
                    ? currentFullPath === item.path 
                    : location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={cn(
                        "flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors",
                        isActive 
                          ? "bg-primary/10 text-primary" 
                          : "hover:bg-muted"
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
